import React from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import downloadIcon from '@/assets/images/download-icon.svg';
import trashBinIcon from '@/assets/images/trash-bin-trash.svg';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { Tag } from '@/components/ui/tag';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

export interface ClientDetailsV2MealPlanListItemProps {
  index: number;
  meal: {
    id: string;
    title: string;
    clientName?: string;
    durationLabel: string;
    tag: string;
    tags?: string[];
    calories: number | null;
    carbs: number | null;
    protein: number | null;
    fat: number | null;
    assignedClients: number | null;
    status: 'Finalized' | 'Draft';
    sourceClientId?: string;
    timelineStatus?: 'past' | 'current' | 'future';
    dateRangeLabel?: string;
  };
  onView: () => void;
  onDownload?: () => void;
  onAssign?: () => void;
  downloading?: boolean;
  onDelete: () => void;
  deleting?: boolean;
  /** When true, hides Draft / Finalized chip (e.g. Generic Meal Plan tab). */
  hideStatusChip?: boolean;
  /** When true, hides the View action button. */
  hideViewAction?: boolean;
  /** When true, hides subtitle text under the title. */
  hideSubtitle?: boolean;
  highlightCurrent?: boolean;
}

export const ClientDetailsV2MealPlanListItem: React.FC<ClientDetailsV2MealPlanListItemProps> = ({
  meal,
  index,
  onView,
  onDownload,
  onAssign,
  downloading = false,
  onDelete,
  deleting = false,
  hideStatusChip = false,
  hideViewAction = false,
  hideSubtitle = false,
  highlightCurrent = false,
}) => {
  const hasCalories = typeof meal.calories === 'number' && Number.isFinite(meal.calories);
  const hasCarbs = typeof meal.carbs === 'number' && Number.isFinite(meal.carbs);
  const hasProtein = typeof meal.protein === 'number' && Number.isFinite(meal.protein);
  const hasFat = typeof meal.fat === 'number' && Number.isFinite(meal.fat);
  const hasAssignedClients = typeof meal.assignedClients === 'number' && Number.isFinite(meal.assignedClients);
  const hasClientMeta = Boolean(meal.clientName) || hasAssignedClients;
  const visibleTags = Array.isArray(meal.tags) ? meal.tags.filter((tag) => tag.trim().length > 0) : [];
  const hasSubtitle = hasCalories || hasCarbs || hasProtein || hasFat || hasClientMeta || visibleTags.length > 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '44px minmax(0, 1fr)' },
        gap: 1.2,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          justifyContent: 'flex-start',
          gap: { xs: 0.7, sm: 0.1 },
          pl: { xs: 0.5, sm: 0 },
          pt: { xs: 0.15, sm: 0.7 },
        }}
      >
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.6rem', lineHeight: 1, fontWeight: 700, color: '#344054' }}>
          {String(index + 1).padStart(2, '0')}
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.06em', color: '#98a2b3' }}>
          MEAL
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: '14px',
          border: highlightCurrent ? '1px solid #86d69c' : '1px solid #dbe6eb',
          bgcolor: highlightCurrent ? '#f0fff4' : '#fff',
          boxShadow: 'none',
          px: { xs: 1.25, sm: 1.6 },
          py: { xs: 1.1, sm: 1.25 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            component="img"
            src={DEFAULT_RECIPE_IMAGE}
            alt={meal.title}
            sx={{
              width: { xs: 64, sm: 76 },
              height: { xs: 64, sm: 76 },
              borderRadius: '50%',
              objectFit: 'cover',
              border: '6px solid #f5f7fa',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.1)',
              flexShrink: 0,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1.25 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  fontWeight: 700,
                  color: '#101828',
                }}
              >
                {meal.title}
              </Typography>
              {meal.durationLabel ? (
                <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'baseline' }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#667085' }}>{meal.durationLabel}</Typography>
                </Box>
              ) : null}
              {meal.dateRangeLabel ? (
                <Tag
                  variant={highlightCurrent ? 'success' : 'neutral'}
                  size="xs"
                  className={`h-[22px] font-[Inter,sans-serif] text-[0.7rem] font-semibold ${
                    highlightCurrent ? 'bg-[#D7F8DF] text-[#137333]' : 'bg-[#F5F7FA] text-[#667085]'
                  }`}
                >
                  {meal.dateRangeLabel}
                </Tag>
              ) : null}
            </Box>
            {!hideSubtitle && hasSubtitle ? (
              <Box sx={{ mt: 0.6, display: 'flex', flexWrap: 'wrap', columnGap: 1.2, rowGap: 0.25 }}>
                {hasCalories ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>
                    Calories: <Box component="span" sx={{ color: '#344054', fontWeight: 600 }}>{meal.calories}</Box>
                  </Typography>
                ) : null}
                {hasCarbs ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>
                    Carbs: <Box component="span" sx={{ color: '#25a8da', fontWeight: 600 }}>{meal.carbs}</Box>
                  </Typography>
                ) : null}
                {hasProtein ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>
                    Protein: <Box component="span" sx={{ color: '#f59e0b', fontWeight: 600 }}>{meal.protein}</Box>
                  </Typography>
                ) : null}
                {hasFat ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>
                    Fat: <Box component="span" sx={{ color: '#22b8b0', fontWeight: 600 }}>{meal.fat}</Box>
                  </Typography>
                ) : null}
                {hasClientMeta ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>
                    Client:{' '}
                    <Box component="span" sx={{ color: '#25a8da', fontWeight: 600 }}>
                      {meal.clientName ? (
                        <ClientNameLink clientId={meal.sourceClientId} className="text-[#25a8da] hover:text-[#25a8da]">
                          {meal.clientName}
                        </ClientNameLink>
                      ) : (
                        `${meal.assignedClients} assigned`
                      )}
                    </Box>
                  </Typography>
                ) : null}
                {visibleTags.length > 0 ? (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#667085', fontWeight: 600 }}>
                    {visibleTags.map((tag) => formatUnderscoreDisplayLabel(tag)).join(', ')}
                  </Typography>
                ) : null}
            </Box>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'flex-start' }, gap: 4 }}>
          {!hideStatusChip ? (
            <Tag
              variant={meal.status === 'Finalized' ? 'info' : 'neutral'}
              size="sm"
              className={`h-6 px-3 font-[Inter,sans-serif] text-[0.78rem] font-semibold ${
                meal.status === 'Finalized' ? 'bg-[#E9F8FD] text-[#1098CC]' : 'bg-[#F5F7FA] text-[#667085]'
              }`}
            >
              {meal.status}
            </Tag>
          ) : null}

          {!hideViewAction ? (
            <Button
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 12 }} />}
              onClick={onView}
              sx={{
                minWidth: 0,
                p: 0,
                m: 0,
                minHeight: 0,
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#1498ca',
                '&:hover': { bgcolor: 'transparent', color: '#0d87b7' },
              }}
            >
              View
            </Button>
          ) : null}

          {onDownload ? (
            <IconButton
              size="small"
              sx={{ color: '#475467', m: 0 }}
              onClick={onDownload}
              disabled={downloading}
              aria-label="Download meal plan"
            >
              {downloading ? (
                <CircularProgress size={16} />
              ) : (
                <img src={downloadIcon} alt="Download" width={22} height={22} style={{ display: 'block' }} />
              )}
            </IconButton>
          ) : null}

          {onAssign ? (
            <Button
              onClick={onAssign}
              sx={{
                minWidth: 0,
                p: 0,
                m: 0,
                minHeight: 0,
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#137333',
                '&:hover': { bgcolor: 'transparent', color: '#0f6b2f' },
              }}
            >
              Assign
            </Button>
          ) : null}

          <IconButton
            size="small"
            sx={{ color: '#f97066', m: 0 }}
            onClick={onDelete}
            disabled={deleting}
            aria-label="Delete meal plan"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : (
              <img src={trashBinIcon} alt="Delete" width={22} height={22} style={{ display: 'block' }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
